import React from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { FaFile, FaLink, FaPlus, FaTrash } from 'react-icons/fa'
import { IFeed_Input_Link } from './types'

interface FeedLinksFormProps {
    links: IFeed_Input_Link[]
    onChange: (links: IFeed_Input_Link[]) => void
    onAddLink: () => void
}

const FeedLinksForm: React.FC<FeedLinksFormProps> = ({ links, onChange, onAddLink }) => {
    const updateLink = (index: number, updates: Partial<IFeed_Input_Link>) => {
        onChange(links.map((link, currentIndex) => (
            currentIndex === index ? { ...link, ...updates } as IFeed_Input_Link : link
        )))
    }

    const handleFileChange = (index: number, file: File | null) => {
        updateLink(index, { file: file || '' })
    }

    const handleDataSourceTypeChange = (index: number, dataSourceType: 'url' | 'file') => {
        const link = links[index]
        updateLink(index, {
            dataSourceType,
            link: dataSourceType === 'url' ? link.link || '' : '',
            file: dataSourceType === 'file' ? link.file : '',
        })
    }

    const handleFileTypeChange = (index: number, fileType: 'csv' | 'xml' | 'json') => {
        updateLink(index, { kind: fileType, position: '' } as Partial<IFeed_Input_Link>)
    }

    const handleRemoveLink = (index: number) => {
        if (links.length > 1) onChange(links.filter((_, currentIndex) => currentIndex !== index))
    }

    return (
        <section className="feed-editor-section" aria-labelledby="feed-sources-heading">
            <div className="feed-section-heading feed-section-heading-action">
                <div className="d-flex align-items-start gap-3">
                    <span className="feed-section-step">2</span>
                    <div>
                        <h5 id="feed-sources-heading" className="mb-1">Data sources</h5>
                        <p className="text-muted small mb-0">Add one or more URLs or files and choose an output format.</p>
                    </div>
                </div>
                <Button type="button" variant="outline-primary" size="sm" onClick={onAddLink}>
                    <FaPlus aria-hidden="true" className="me-2" />
                    Add source
                </Button>
            </div>

            <div className="feed-builder-list">
                {links.map((link, index) => {
                    const sourceId = link.id || `source-${index}`
                    const isFileSource = link.dataSourceType === 'file'
                    const hasStoredFile = typeof link.file === 'string' && Boolean(link.file)

                    return (
                        <div className="feed-builder-card" key={sourceId}>
                            <div className="feed-builder-card-header">
                                <div className="d-flex align-items-center gap-2 min-w-0">
                                    <span className="feed-builder-card-icon">
                                        {isFileSource ? <FaFile aria-hidden="true" /> : <FaLink aria-hidden="true" />}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="fw-semibold text-truncate">{link.name || `Source ${index + 1}`}</div>
                                        <div className="text-muted small">Source {index + 1}</div>
                                    </div>
                                </div>
                                {links.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="feed-remove-icon"
                                        onClick={() => handleRemoveLink(index)}
                                        aria-label={`Remove source ${index + 1}`}
                                        title="Remove source"
                                    >
                                        <FaTrash aria-hidden="true" />
                                    </Button>
                                )}
                            </div>

                            <Row className="g-3">
                                <Col xs={12} md={6} lg={4}>
                                    <Form.Group controlId={`feed-source-name-${sourceId}`}>
                                        <Form.Label>Source name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="For example: Main catalog"
                                            value={link.name || ''}
                                            onChange={(event) => updateLink(index, { name: event.target.value })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={3} lg={2}>
                                    <Form.Group controlId={`feed-source-type-${sourceId}`}>
                                        <Form.Label>Source type</Form.Label>
                                        <Form.Select
                                            value={link.dataSourceType || 'url'}
                                            onChange={(event) => handleDataSourceTypeChange(index, event.target.value as 'url' | 'file')}
                                        >
                                            <option value="url">URL</option>
                                            <option value="file">File</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={3} lg={2}>
                                    <Form.Group controlId={`feed-input-format-${sourceId}`}>
                                        <Form.Label>Input format</Form.Label>
                                        <Form.Select
                                            value={link.kind || 'csv'}
                                            onChange={(event) => handleFileTypeChange(index, event.target.value as 'csv' | 'xml' | 'json')}
                                        >
                                            <option value="csv">CSV</option>
                                            <option value="xml">XML</option>
                                            <option value="json">JSON</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} md={6} lg={4}>
                                    <Form.Group controlId={`feed-source-location-${sourceId}`}>
                                        <Form.Label>{isFileSource ? 'Source file' : 'Source URL'}</Form.Label>
                                        {!isFileSource ? (
                                            <Form.Control
                                                key="url-source"
                                                type="url"
                                                placeholder="https://example.com/products.csv"
                                                value={link.link || ''}
                                                onChange={(event) => updateLink(index, { link: event.target.value })}
                                                required
                                            />
                                        ) : hasStoredFile ? (
                                            <div className="feed-stored-file">
                                                <span className="text-truncate" title={String(link.file)}>{String(link.file)}</span>
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    onClick={() => updateLink(index, { file: '' })}
                                                >
                                                    Replace
                                                </Button>
                                            </div>
                                        ) : (
                                            <Form.Control
                                                key="file-source"
                                                type="file"
                                                accept=".csv,.xml,.json,text/csv,application/xml,text/xml,application/json"
                                                required={!link.file}
                                                onChange={(event) => {
                                                    const input = event.currentTarget as HTMLInputElement
                                                    handleFileChange(index, input.files?.[0] || null)
                                                }}
                                            />
                                        )}
                                    </Form.Group>
                                </Col>

                                {(link.kind === 'xml' || link.kind === 'json') && (
                                    <Col xs={12} md={6} lg={4}>
                                        <Form.Group controlId={`feed-data-location-${sourceId}`}>
                                            <Form.Label>Product list location <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="For example: data.products"
                                                value={link.position || ''}
                                                onChange={(event) => updateLink(index, { position: event.target.value } as Partial<IFeed_Input_Link>)}
                                            />
                                            <Form.Text className="text-muted">Path to the product array in the source.</Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}

                                <Col xs={12} sm={6} md={4} lg={3}>
                                    <Form.Group controlId={`feed-export-format-${sourceId}`}>
                                        <Form.Label>Export as</Form.Label>
                                        <Form.Select
                                            value={link.exportAs || 'csv'}
                                            onChange={(event) => updateLink(index, {
                                                exportAs: event.target.value as 'csv' | 'xml' | 'xmlRss' | 'json',
                                            })}
                                        >
                                            <option value="csv">CSV</option>
                                            <option value="xml">XML</option>
                                            <option value="xmlRss">XML (RSS)</option>
                                            <option value="json">JSON</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    )
                })}
            </div>
        </section>
    )
}

export default FeedLinksForm
